import React, { FC, ReactElement } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import { clsx } from 'clsx';
// Components
import Text from '../../text';
import Space from '../../layout/space';
import SvgIcon from '../../icons/SvgIcon';
import { IconCommon } from '../../../resources/images';
import { getBottomBarHeight, getTopBarHeight } from '../../../localStorageAPI';

export interface ModalHeaderProps {
    title: string;
    label?: string;
    onClose: () => void;
    subtitle?: string;
    floatingClose?: 'none' | 'center' | 'topRight';
}

// Round close button: a 24px icon with 8px of padding on each side.
const FLOATING_CLOSE_SIZE = 40;
// Height of the button plus a gap, so the cross lands just above the sheet instead of on top of
// its first rows.
const FLOATING_CLOSE_OFFSET = FLOATING_CLOSE_SIZE + 12;
const FLOATING_CLOSE_MIN_TOP = 10;

const ModalHeader: FC<ModalHeaderProps> = ({ title, label, onClose, subtitle, floatingClose }): ReactElement => {
    const classes = useStyles();
    const isFloating = !!floatingClose && floatingClose !== 'none';

    const floatingRef = React.useRef<HTMLSpanElement>(null);
    const [floatingTop, setFloatingTop] = React.useState<number | null>(null);

    // The centered cross floats over the backdrop but is painted inside the dialog paper, so its
    // position has to be measured. Measuring the paper - not the header, which moves as soon as the
    // content around it renders - is what keeps it above the sheet, and the observer catches every
    // later layout change: content growing, or the native app resizing the webview.
    React.useLayoutEffect(() => {
        if (floatingClose !== 'center') {
            return undefined;
        }

        const paper = floatingRef.current?.closest('.MuiPaper-root');
        if (!paper) {
            return undefined;
        }

        const updateFloatingTop = (): void => {
            const { top } = paper.getBoundingClientRect();
            // Clamped at both ends so the button stays whole and reachable whatever the sheet does -
            // a sheet taller than the viewport, or one still off screen when first measured. The
            // lower bound also steps over the Android system bar, which overlays the webview.
            const lowestTop = window.innerHeight - getBottomBarHeight() - FLOATING_CLOSE_SIZE - FLOATING_CLOSE_MIN_TOP;
            const maxTop = Math.max(FLOATING_CLOSE_MIN_TOP, lowestTop);
            const wantedTop = Math.max(FLOATING_CLOSE_MIN_TOP, top - FLOATING_CLOSE_OFFSET);
            setFloatingTop(Math.min(wantedTop, maxTop));
        };

        updateFloatingTop();

        const observer = new ResizeObserver(updateFloatingTop);
        observer.observe(paper);
        window.addEventListener('scroll', updateFloatingTop);
        window.addEventListener('resize', updateFloatingTop);

        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', updateFloatingTop);
            window.removeEventListener('resize', updateFloatingTop);
        };
    }, [floatingClose]);

    const getFloatingStyle = (): React.CSSProperties => {
        if (floatingClose === 'topRight') {
            return { top: 28 + getTopBarHeight() };
        }
        // Before the first measurement, the fallback `top` of the `center` class applies.
        return floatingTop === null ? {} : { top: floatingTop };
    };

    return (
        <DialogTitle className={classes.modalHeaderRoot}>
            {isFloating && (
                <span ref={floatingRef} className={classes[floatingClose]} style={getFloatingStyle()}>
                    <div onClick={onClose}>
                        <SvgIcon icon={IconCommon.Close} color="allBlack" fill="none" size="large" />
                    </div>
                </span>
            )}
            <div className={clsx(classes.modalHeaderContainer, 'modalHeaderContainer')}>
                <Text.H05 text={title} />
                {label && <Text.H4 text={label} />}
                {!isFloating && (
                    <IconButton className={classes.closeButton} aria-label="close" onClick={onClose} size="large">
                        <CloseIcon />
                    </IconButton>
                )}
            </div>
            {subtitle && (
                <div className={classes.modalHeaderSubtitle}>
                    <Space value={2} />
                    <Text.Description16 customColor="grey" text={subtitle} />
                </div>
            )}
        </DialogTitle>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        modalHeaderRoot: {
            margin: 0,
            padding: 0,
            paddingBottom: theme.spacing(3),
        },
        modalHeaderContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        modalHeaderSubtitle: {},
        closeButton: {
            padding: 0,
            color: theme.palette.primary.main,
        },
        center: {
            position: 'fixed',
            top: 'calc(7.5% - 20px)',
            left: 'calc(50% - 20px)',
            padding: '8px',
            backgroundColor: 'white',
            borderRadius: '20px',
            '& > div': {
                display: 'flex',
            },
        },
        topRight: {
            position: 'fixed',
            right: 20,
            padding: '8px',
            borderRadius: '20px',
            '& > div > svg': {
                stroke: 'white',
            },
        },
    }),
);

export default ModalHeader;
